import React from 'react';
import {DefaultValues, FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {Button} from '@edna/components';
import {Stack} from '@edna/components/primitives';
import {useOnChange} from '@edna/hooks';
import CleanFiltersIcon from '@edna/icons/cleanFilters.svg';
import CloseIcon from '@edna/icons/close.svg';

import * as S from './style';

interface IFiltersProps<T> {
  filters: T;
  activeFiltersCount?: number;
  resetFilters: TEmptyFunction;
  toggleFilter: TEmptyFunction;
  setFilters: React.Dispatch<React.SetStateAction<T>>;
  children: React.ReactNode;
  dataSelector?: string;
  className?: string;
}

const FiltersFormGeneric = <T extends TAnyObject>({
  toggleFilter,
  activeFiltersCount,
  resetFilters,
  setFilters,
  filters,
  children,
  dataSelector,
  className,
}: IFiltersProps<T>) => {
  const {t} = useTranslation();

  const methods = useForm<T>({
    mode: 'all',
    defaultValues: {...filters} as DefaultValues<T>,
  });

  useOnChange(() => methods.reset(filters), [filters]);

  const updateFilters = React.useCallback(
    (values: T) => {
      setFilters((prevState) => ({
        ...prevState,
        ...values,
      }));
      toggleFilter();
    },
    [setFilters, toggleFilter],
  );

  const testIdProps = dataSelector ? edna.renderTestId(dataSelector) : null;

  return (
    <FormProvider {...methods}>
      <S.Filters
        onSubmit={methods.handleSubmit(updateFilters)}
        className={className}
        {...testIdProps}
      >
        <Stack direction="ROW" size="7" justify="space-between" isFullWidth>
          {children}
        </Stack>
        <S.FormFooter>
          <S.LeftButton name="cancel" disabled={!activeFiltersCount} onClick={resetFilters}>
            <S.Icon as={CleanFiltersIcon} size="16px" title={t('Filters:resetFilters')} />
            {t('Common:button.resetAll')}
          </S.LeftButton>
          <Button appearance="secondary" onClick={toggleFilter}>
            <S.Icon as={CloseIcon} size="20px" title={t('Filters:resetFilters')} />
            {t('Common:button.close')}
          </Button>
          <Button appearance="primary" type="submit">
            {t('Common:button.apply')}
          </Button>
        </S.FormFooter>
      </S.Filters>
    </FormProvider>
  );
};

FiltersFormGeneric.displayName = 'FiltersFormGeneric';

export const Filters = React.memo(FiltersFormGeneric) as typeof FiltersFormGeneric;
